import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, lastValueFrom, map, merge, take } from 'rxjs';
import { AppResource } from 'src/app/app.resource';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { Etage, SaisieHabitation, TypeHabitation } from 'src/app/core/models/form-state.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { Stepper } from 'src/app/core/models/stepper.model';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import { DEPENDENCY_PATH } from 'src/app/shared/constants/route.constants';
import { PopUpInfoVM } from 'src/app/shared/models/components/popupinfoVm';
import { QuestionVM } from 'src/app/shared/models/components/questionVm';
import { State } from '../../../core/state/core.state';
import * as fromHousing from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';

export interface IdFloorValue {
  id: Etage;
  name: string;
}
@Component({
  selector: 'app-number-roommate',
  templateUrl: './number-roommate.component.html',
  styles: ''
  // standalone: true,
  // imports: [WebCallbackPopupComponent]
})
export class NumberRoommateComponent extends BaseComponent implements OnInit {
  mouseYesOvered = false;
  mouseNoOvered = false;

  yesSelected = false;
  noSelected = false;

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepNumberRoommates,
    subStepNumber: 6
  };

  public override currentPage = MatomoConstants.NOMBRE_COLOCATAIRE;

  questionFloor = new QuestionVM(this.resource.question.numberRoommates);
  popupInfoFloor = new PopUpInfoVM([this.resource.infobulle.numberRoommates]);
  roommatesForm!: FormGroup<{ numberOfRoommates: FormControl<number> }>;

  displayModal: boolean = false;
  ready: boolean = false;
  habitation: TypeHabitation | undefined;
  path!: PathType;

  PathType = PathType;
  max: number = 7; // 7 roommates max

  constructor(
    store: Store<State>,
    resources: AppResource,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef
  ) {
    super(store, resources);
  }

  override async ngOnInit() {
    this.updateResumeStepVisibility(true);
    await this.initForm();
    super.ngOnInit();
  }

  async initForm() {
    const maxPiecesFromState = await lastValueFrom(
      this.store.select(fromHousing.selectNumberPieces).pipe(take(1))
    );

    this.roommatesForm = new FormGroup(
      {
        numberOfRoommates: new FormControl<number>(0, {
          validators: [Validators.required, this.maxValueValidator(maxPiecesFromState!)],
          nonNullable: true
        })
      },
      {
        updateOn: 'change',
        validators: []
      }
    );

    this.roommatesForm.valueChanges
      .pipe(
        map(() => this.roommatesForm.valid),
        distinctUntilChanged()
      )
      .subscribe((isValid: boolean) => {
        this.store.dispatch(fromHousing.patchNumberRoomsValidity({ isValid }));
      });

    const numberOfRoommates$ = this.roommatesForm.controls.numberOfRoommates.valueChanges.pipe(
      map((val: number) => {
        const NombreColocataires = val;
        return { NombreColocataires } as Partial<SaisieHabitation>;
      })
    );

    merge(numberOfRoommates$).subscribe((payload: Partial<SaisieHabitation>) => {
      this.store.dispatch(fromHousing.patchNumberOfPeople({ payload }));
    });

    this.ready = true;
  }

  cancel() {
    this.displayModal = false;
    this.initForm();
    this.yesSelected = false;
    this.noSelected = false;
  }

  async nextStep() {
    if (this.roommatesForm.valid) {
      await this.updateResumeStep();
      await this.updateStepper();
      this.router.navigate([DEPENDENCY_PATH]);
    } else {
      this.displayModal = true;
    }
  }

  async updateStepper() {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));

    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.numberRoommates
    );

    if (subtitleS) subtitleS.isValid = true;

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep() {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );

    const tmpResumeStep = cloneResumeStep(resumeStep);

    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }

  // #region Validator
  maxValueValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      let maximumPieces = this.checkMaxPiecesAvailables(max);
      if (value > maximumPieces) return { maxValue: true };
      else return null;
    };
  }
  // #endregion

  private checkMaxPiecesAvailables(max: number): number {
    switch (max) {
      case 1:
      case 2:
        return 1;
      case 3:
        return 2;
      case 4:
        return 3;
      case 5:
        return 4;
      case 6:
        return 5;
      case 7:
      case 8:
        return 6;
      default:
        return 1;
    }
  }
}
